import React, { ReactElement, CSSProperties, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./FormGen.module.css";
import Button from "../Button/Button";
import InputContainer from "../InputContainer/InputContainer";
import Alert from "../Alert/Alert";
import MandatoryFieldText from "../MandatoryFieldText/MandatoryFieldText";
import SmartValidation from "../SmartValidation/SmartValidation";


interface BaseProps {
  name: string,
  label: string,
  exampleText?: string,
  placeholder?: string,
  required: boolean,
  readOnly?: boolean,
  id?: string,
  conditional?: {
    watch: string,
    value: string
  },  
  validate?: any,
  smartValidationRules?:SmartValidationRule[],
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  type: "text" | "textarea" | "number" | "component" | "password",
}

interface NumberInput extends BaseProps {
  type: "number",
  min?: number,
  max?: number,
}

interface TextInput extends BaseProps {
  type: "text" | "textarea" | "password",
  minLength?: number,
  maxLength?: number,  
  pattern?: RegExp,
  patternErrorMessage?: String,
}

interface ComponenInput extends BaseProps {
  type: "component"
  component: ReactElement
}

export type InputObject = NumberInput | TextInput | ComponenInput

type FormButton = {
  onClick: (arg0?: any) => void,
  text: string,
  name: string,
  color: "red" | "grey" | "blue" | "green",
  style?: CSSProperties,
}

export type SettingsObject = {
  cancelButton?: FormButton,
  saveButton?: FormButton,
  submitButton: FormButton,
  resetButton?: boolean,
  checkDirty?: boolean,
  buttonRowStyle?: CSSProperties,
  hideMandatoryFieldText?: boolean,
  hideUnsavedChangesBanner?: boolean
  disableFormReset?:boolean
}

interface FormGenProps {
  inputs: InputObject[];
  settings: SettingsObject;
  defaultValues?: any;
  style?: CSSProperties;
  reValidate?: boolean;
}

interface SmartValidationRule {
  valid:boolean,
  pattern:RegExp,
  label:string
}

export const FormGen: React.FC<FormGenProps> = ({
  inputs,
  settings,
  defaultValues,
  children,
  style,
  reValidate,
  ...rest
}): ReactElement => {
  const { control, register, handleSubmit, reset, watch, formState, errors, getValues, triggerValidation } = useForm(
    { defaultValues } || {}
  );
  const { dirty, isValid } = formState;
  const onSubmit = (data: any) => {
    if(!settings.disableFormReset){
      isValid &&
        reset({
          dirty: false,
        });
    }
    settings.submitButton.onClick(data);
  };

  useEffect(()=>{
    if(reValidate){
      triggerValidation();
    }
  },[reValidate]);

  return (
    <>
      {dirty && !settings.hideUnsavedChangesBanner && (
        <Alert
          title="Unsaved Changes"
          message="You must save your changes before navigating to another page. If you do not select save, all changes will be lost."
          color="red"
        />
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        {...rest}
        className={styles.Form}
        style={{ ...style }}
      >
        {children}
        {!settings.hideMandatoryFieldText && <MandatoryFieldText />}
        <div className={styles.form_inputs}>
          {inputs.map((input: any) =>
            !input.conditional ||
            (input.conditional && watch(input.conditional.watch) === input.conditional.value) ? (
              <InputContainer
                key={input.name}
                forLabel={input.name}
                required={input.required}
                exampleText={input.exampleText}
                errorText={errors[input.name] ? errors[input.name].message : null}
                labelText={input.label}
              >
                {input.smartValidationRules && (
                    <SmartValidation 
                      value={watch(input.name)}
                      rules={input.smartValidationRules}
                    />
                )}
                
                {input.type === "text" || input.type === "number" || input.type === "password" ? (
                  <input
                    name={input.name}
                    className={errors[input.name] ? styles.error : ""}
                    id={input.id}
                    type={input.type}
                    readOnly={input.readOnly || false}
                    style={input.style}
                    placeholder={input.placeholder}
                    onChange={input.onChange}
                    ref={register({
                      required: {
                        value: input.required,
                        message: "Must not be blank",
                      },
                      pattern: {
                        value: input.pattern || /.*/,
                        message: input.patternErrorMessage || "",
                      },
                      minLength: {
                        value: input.minLength || 0,
                        message: `Must be atleast ${input.minLength} characters long`,
                      },
                      maxLength: {
                        value: input.maxLength || 9999,
                        message: `Must be no more than ${input.maxLength} characters long`,
                      },
                      min: {
                        value: input.min || -9999,
                        message: `Value must be equal to atleast ${input.min}`,
                      },
                      max: {
                        value: input.max || 9999,
                        message: `Value must not equal more than ${input.max}`,
                      },
                      validate: input.validate || { empty: () => {} },
                    })}
                  />
                ) : input.type === "textarea" ? (
                  <textarea
                    name={input.name}   
                    id={input.id}
                    className={errors[input.name] ? styles.error : ""}
                    readOnly={input.readOnly || false}
                    style={input.style}
                    placeholder={input.placeholder}
                    onChange={input.onChange}
                    ref={register({
                      required: {
                        value: input.required,
                        message: "Must not be blank",
                      },
                      pattern: {
                        value: input.pattern || /.*/,
                        message: input.patternErrorMessage || "",
                      },
                      minLength: {
                        value: input.minLength || 0,
                        message: `Must be atleast ${input.minLength} characters long`,
                      },
                      maxLength: {
                        value: input.maxLength || 9999,
                        message: `Must be no more than ${input.maxLength} characters long`,
                      },
                      validate: input.validate || { empty: () => {} },
                    })}
                  />
                ) : input.component ? (
                  <Controller
                    className={`${styles.controlled_input} ${
                      errors[input.name] ? styles.error : ""
                    }`}
                    id={input.id}
                    as={input.component}
                    name={input.name}
                    valueName={input.name}
                    onChange={([selected]) => selected}
                    control={control}
                    rules={{
                      required: {
                        value: input.required,
                        message: "Must not be blank",
                      },
                      pattern: {
                        value: input.pattern || /.*/,
                        message: input.patternErrorMessage || "",
                      },
                      minLength: {
                        value: input.minLength || 0,
                        message: `Must be atleast ${input.minLength} characters long`,
                      },
                      maxLength: {
                        value: input.maxLength || 9999,
                        message: `Must be no more than ${input.maxLength} characters long`,
                      },
                      validate: input.validate || { empty: () => {} },
                    }}
                  />
                ) : null}
              </InputContainer>
            ) : null
          )}
        </div>
        <div className={styles.button_row} style={settings.buttonRowStyle}>
          {settings.cancelButton && (
            <Button
              color={settings.cancelButton.color}
              text={settings.cancelButton.text}
              name={settings.cancelButton.name}
              onClick={settings.cancelButton.onClick}
              style={settings.cancelButton.style}
            />
          )}
          <div className={styles.button_group}>
            {settings.saveButton && (
              <Button
                color={settings.saveButton.color}
                text={settings.saveButton.text}
                name={settings.saveButton.name}
                onClick={(e) => {
                  e.preventDefault();
                  settings.saveButton.onClick(getValues());
                }}
                style={settings.saveButton.style}
              />
            )}
            {settings.submitButton && (
              <Button
                color={settings.submitButton.color}
                text={settings.submitButton.text}
                name={settings.submitButton.name}
                style={settings.submitButton.style}
              />
            )}
          </div>
        </div>
      </form>
    </>
  );
};


